import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty, Statistic,
  Switch, Progress, Tooltip, Table,
} from 'antd';
import {
  WifiOutlined, ReloadOutlined, ThunderboltOutlined, CloudOutlined, DatabaseOutlined,
  EnvironmentOutlined, SignalFilled, ApiOutlined,
} from '@ant-design/icons';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, AreaChart, Area, Cell } from 'recharts';
import { getEvents, getReaderTelemetry, getAntennaStats, getReadRate } from '../api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

const HEALTH = {
  healthy: { color: '#10b981', label: 'HEALTHY', icon: '✅' },
  idle:    { color: '#f59e0b', label: 'IDLE',    icon: '⏸️' },
  stale:   { color: '#ef4444', label: 'STALE',   icon: '⚠️' },
  offline: { color: '#94a3b8', label: 'OFFLINE', icon: '⚪' },
  unknown: { color: '#94a3b8', label: 'UNKNOWN', icon: '❓' },
};

const CONN_ICON = { LAN: <DatabaseOutlined />, WiFi: <WifiOutlined />, '4G': <CloudOutlined />, '5G': <CloudOutlined /> };

export default function ReaderTelemetry() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [readers, setReaders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [antennas, setAntennas] = useState([]);
  const [readRate, setReadRate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    getReaderTelemetry({ event_id: eventId })
      .then(r => {
        const data = r.data.data || [];
        setReaders(data);
        if (!selected && data.length) setSelected(data[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId, selected]);

  useEffect(() => {
    load();
    if (autoRefresh && eventId) intervalRef.current = setInterval(load, 5000);
    return () => clearInterval(intervalRef.current);
  }, [load, autoRefresh, eventId]);

  // Load detail data when a reader is selected
  useEffect(() => {
    if (!selected || !eventId) return;
    Promise.all([
      getAntennaStats({ event_id: eventId, reader_id: selected.id }),
      getReadRate({ event_id: eventId, reader_id: selected.id, minutes: 60 }),
    ]).then(([a, r]) => {
      setAntennas(a.data.data || []);
      setReadRate(r.data.data || []);
    }).catch(() => {});
  }, [selected, eventId]);

  // Aggregate KPIs
  const totalReaders = readers.length;
  const healthyReaders = readers.filter(r => r.health === 'healthy').length;
  const totalReads = readers.reduce((s, r) => s + (r.total_reads || 0), 0);
  const totalTPS = readers.reduce((s, r) => s + (r.tps || 0), 0).toFixed(1);

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <SignalFilled style={{ marginRight: 8, color: TEAL }} />Reader Telemetry
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Per-reader health, throughput and signal stats · auto-refresh every 5s
          </Text>
        </Col>
        <Col>
          <Space>
            <Space size={6}><Text style={{ fontSize: 12 }}>Auto-refresh</Text>
              <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" /></Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* Aggregate KPIs */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Total Readers" value={totalReaders} prefix={<ApiOutlined />} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #10b981' }}>
            <Statistic title="Healthy" value={healthyReaders}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {totalReaders}</Text>}
              valueStyle={{ color: '#10b981' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Total Reads" value={totalReads.toLocaleString()} valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #f59e0b' }}>
            <Statistic title="Total TPS" value={totalTPS}
              suffix={<Text type="secondary" style={{ fontSize: 11 }}>tags/sec</Text>}
              prefix={<ThunderboltOutlined />} valueStyle={{ color: '#f59e0b' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Reader list */}
        <Col xs={24} lg={selected ? 12 : 24}>
          <Card title="Reader Fleet" style={{ borderRadius: 10 }}>
            {readers.length === 0 ? <Empty /> : (
              <div style={{ display: 'grid', gap: 10 }}>
                {readers.map(r => {
                  const h = HEALTH[r.health] || HEALTH.unknown;
                  const isSel = selected?.id === r.id;
                  return (
                    <div key={r.id} onClick={() => setSelected(r)}
                      style={{
                        padding: 12, borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${isSel ? TEAL : '#e5e7eb'}`,
                        background: isSel ? '#f0fdfd' : '#fff',
                        transition: 'all 0.15s',
                      }}>
                      <Row align="middle" gutter={[8, 4]}>
                        <Col flex="auto">
                          <Space>
                            <span style={{ fontSize: 18 }}>{h.icon}</span>
                            <Text strong>{r.reader_name}</Text>
                            <Tag color="default" style={{ fontSize: 10 }}>{r.reader_code}</Tag>
                            {r.connectivity && <Tag>{CONN_ICON[r.connectivity]} {r.connectivity}</Tag>}
                          </Space>
                          <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                            <EnvironmentOutlined /> {r.location_name || 'No location'} ·
                            {r.last_seen ? ` Last seen ${dayjs(r.last_seen).fromNow()}` : ' Never seen'}
                          </div>
                        </Col>
                        <Col>
                          <Tag style={{ background: h.color, color: '#fff', border: 'none', fontWeight: 700 }}>{h.label}</Tag>
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: 8 }}>
                        <Col xs={6}><Tag color="blue" style={{ width: '100%', textAlign: 'center' }}>📊 {(r.total_reads || 0).toLocaleString()}</Tag></Col>
                        <Col xs={6}><Tag color="cyan" style={{ width: '100%', textAlign: 'center' }}>📡 {r.unique_tags || 0} tags</Tag></Col>
                        <Col xs={6}><Tag color="orange" style={{ width: '100%', textAlign: 'center' }}>⚡ {r.tps || 0} TPS</Tag></Col>
                        <Col xs={6}><Tag color="purple" style={{ width: '100%', textAlign: 'center' }}>📶 {r.avg_rssi ? `${r.avg_rssi} dBm` : '—'}</Tag></Col>
                      </Row>
                      <Row style={{ marginTop: 6 }}>
                        <Col flex="auto">
                          <Text style={{ fontSize: 10, color: '#64748b' }}>
                            Reads in last 5 min: <strong>{r.reads_last_5min || 0}</strong> · last min: <strong>{r.reads_last_1min || 0}</strong> · antennas active: <strong>{r.antennas_active || 0}</strong>
                          </Text>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        {/* Selected reader detail */}
        {selected && (
          <Col xs={24} lg={12}>
            {/* Read-rate sparkline */}
            <Card title={<Space><ThunderboltOutlined style={{ color: '#f59e0b' }} /> Read Rate (last hour)
              · <Text type="secondary" style={{ fontSize: 11 }}>{selected.reader_name}</Text></Space>}
              style={{ borderRadius: 10, marginBottom: 16 }}>
              {readRate.length === 0 ? <Empty description="No reads in the last hour" /> : (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={readRate} margin={{ top: 8, right: 0, bottom: 0, left: -20 }}>
                    <defs>
                      <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={TEAL} stopOpacity={0.7}/>
                        <stop offset="100%" stopColor={TEAL} stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="minute" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip />
                    <Area type="monotone" dataKey="reads" stroke={TEAL} fill="url(#rateGrad)" name="Reads/min" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* Per-antenna stats */}
            <Card title={<Space><ApiOutlined style={{ color: PRIMARY }} /> Antenna Performance</Space>}
              style={{ borderRadius: 10 }}>
              {antennas.length === 0 ? <Empty description="No antenna data" /> : (
                <Table
                  dataSource={antennas}
                  rowKey="antenna_port"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: 'Port', dataIndex: 'antenna_port', width: 60, align: 'center',
                      render: v => <Tag color="blue" style={{ fontWeight: 700 }}>📡 {v}</Tag> },
                    { title: 'Reads', dataIndex: 'read_count', align: 'right',
                      render: v => v.toLocaleString() },
                    { title: 'Unique Tags', dataIndex: 'unique_tags', align: 'right' },
                    { title: 'Avg RSSI', dataIndex: 'avg_rssi', width: 100, align: 'right',
                      render: v => <Text style={{ color: v >= -65 ? '#10b981' : v >= -75 ? '#f59e0b' : '#ef4444' }}>{v} dBm</Text> },
                    { title: 'Range', width: 110, align: 'right',
                      render: (_, r) => <Text type="secondary" style={{ fontSize: 11 }}>{r.min_rssi} → {r.max_rssi}</Text> },
                    { title: 'Util', dataIndex: 'utilization_pct', width: 110,
                      render: v => <Progress percent={v} size="small" strokeColor={TEAL} /> },
                  ]}
                />
              )}
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}
